/**
 * BulkActionsPanel Template
 *
 * Displays bulk action controls when items are selected
 * Features dropdown menu with action confirmation
 * Includes undo functionality and responsive design
 */

"use client";

import React from "react";

import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@nextui-org/react";
import { AlertTriangle } from "lucide-react";
import { useTranslations } from "next-intl";

import { BulkAction } from "@/hooks/useBulkActions";
import IconDownload from "@/icons/content/media/download.svg";
import IconRefresh from "@/icons/loaderBtn.svg";
import IconAdd from "@/icons/table/table-plus.svg";
import IconAssign from "@/icons/table/table-user.svg";
import IconApprove from "@/icons/ui/buttons/check.svg";
import { cn } from "@/utils/cn";

import styles from "./BulkActionsPanel.module.scss";

export interface BulkActionsPanelProps {
  /** Number of selected items */
  selectedCount: number;
  /** Available bulk actions */
  actions: BulkAction[];
  /** Action execution handler */
  onActionExecute: (actionId: string) => Promise<void>;
  /** Clear selection handler */
  onClearSelection: () => void;
  /** Undo handler (optional) */
  onUndo?: () => void;
  /** Can undo state */
  canUndo?: boolean;
  /** Last action label for undo */
  undoActionLabel?: string;
  /** Loading state */
  isLoading?: boolean;
  /** Panel position */
  position?: "top" | "bottom" | "floating";
  /** Total items */
  totalCount?: number;
  /** Custom className */
  className?: string;
  /** Hide when no selection */
  hideWhenEmpty?: boolean;
  /** Compact mode */
  compact?: boolean;
}

export function BulkActionsPanel({
  selectedCount,
  actions,
  onActionExecute,
  onClearSelection,
  onUndo,
  canUndo = false,
  undoActionLabel,
  isLoading = false,
  position = "floating",
  totalCount = 0,
  className,
  hideWhenEmpty = true,
  compact = false,
}: BulkActionsPanelProps) {
  const t = useTranslations("search");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedAction, setSelectedAction] = React.useState<BulkAction | null>(
    null,
  );
  const [isExecuting, setIsExecuting] = React.useState(false);

  // Don't render if no items selected and hideWhenEmpty is true
  if (selectedCount === 0 && hideWhenEmpty) {
    return null;
  }

  // Show/hide state for animation
  const isVisible = selectedCount > 0;

  // Handle action selection
  const handleActionSelect = (action: BulkAction) => {
    if (action.requiresConfirmation) {
      setSelectedAction(action);
      onOpen();
    } else {
      executeAction(action);
    }
  };

  // Execute action
  const executeAction = async (action: BulkAction) => {
    if (action.disabled || isExecuting) return;

    setIsExecuting(true);
    try {
      await onActionExecute(action.id);
    } finally {
      setIsExecuting(false);
      onClose();
      setSelectedAction(null);
    }
  };

  // Handle confirmation
  const handleConfirmAction = () => {
    if (selectedAction) {
      executeAction(selectedAction);
    }
  };

  // Filter enabled actions
  const enabledActions = actions.filter((action) => !action.disabled);

  // Panel styles based on position
  const panelStyles = {
    top: styles.positionTop,
    bottom: styles.positionBottom,
    floating: styles.positionFloating,
  };

  return (
    <>
      {/* Main Panel */}
      <div
        className={cn(
          styles.bulkActionsPanel,
          panelStyles[position],
          !isVisible && styles.hidden,
          compact && styles.compact,
          className,
        )}
      >
        {/* Header text: Total + Selected */}
        <div className={styles.headerText}>
          <span>{t("table.bulk.total", { count: totalCount })}</span>
          <span className={styles.dotDivider} />
          <span>{t("table.bulk.selected", { count: selectedCount })}</span>
        </div>

        {/* Icon actions row (Figma-like). Pixel-perfect spacing handled in SCSS */}
        <div className={styles.iconBar}>
          {enabledActions.map((action) => {
            const icon =
              action.id === "actualize" ? (
                <IconRefresh
                  width={18}
                  height={18}
                />
              ) : action.id === "export" ? (
                <IconDownload
                  width={18}
                  height={18}
                />
              ) : action.id === "approve" ? (
                <IconApprove
                  width={18}
                  height={18}
                />
              ) : action.id === "assign" ? (
                <IconAssign
                  width={18}
                  height={18}
                />
              ) : action.id === "add" ? (
                <IconAdd
                  width={18}
                  height={18}
                />
              ) : (
                action.icon
              );
            const title = t(`table.bulk.action.${action.id}`);
            return (
              <button
                key={action.id}
                type="button"
                aria-label={title}
                className={styles.iconButton}
                onClick={() => handleActionSelect(action)}
                disabled={selectedCount === 0 || !!action.disabled}
                title={title}
              >
                {icon}
              </button>
            );
          })}
        </div>
        {/* No dropdown: only text + 4 icons per Figma */}

        {/* Undo hidden for strict Figma layout */}

        {/* Clear button hidden in this layout */}
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        placement="center"
        size="md"
      >
        <ModalContent>
          <ModalHeader className="flex items-center gap-2">
            {selectedAction?.isDestructive && (
              <AlertTriangle
                className="text-red-500"
                size={20}
              />
            )}
            {t("table.bulk.confirm.title")}
          </ModalHeader>
          <ModalBody>
            <p className="text-gray-700">
              {selectedAction?.confirmMessage ||
                t("table.bulk.confirm.message", {
                  action: selectedAction?.label || "",
                  count: selectedCount,
                })}
            </p>
            {selectedAction?.isDestructive && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                <p className="text-red-800 text-sm font-medium">
                  {t("table.bulk.confirm.warning")}
                </p>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={onClose}
              disabled={isExecuting}
            >
              {t("table.bulk.confirm.cancel")}
            </Button>
            <Button
              color={selectedAction?.isDestructive ? "danger" : "primary"}
              onPress={handleConfirmAction}
              isLoading={isExecuting}
              className={cn(
                !selectedAction?.isDestructive && styles.confirmButton,
              )}
            >
              {t("table.bulk.confirm.ok")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default BulkActionsPanel;
